import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, X, ArrowRight, Plus } from "lucide-react";
import { SEOHead, productSchema, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDONS = [
  { name: "Advanced SEO Package", price: "$299", desc: "Full SEO setup and analysis to get you found on Google from day one" },
  { name: "Professional Copywriting", price: "$199", desc: "Professionally written website copy for all pages" },
  { name: "Logo Design", price: "$149", desc: "Custom logo + brand colours + style guide" },
  { name: "Website Hosting", price: "$99/yr", desc: "Fast Australian hosting, SSL, backups & support — billed annually" },
  { name: "Google Ads Setup", price: "$299", desc: "Set up & optimise your first Google Ads campaign" },
  { name: "Extra Pages", price: "$79/ea", desc: "Add more pages to your website anytime" },
];

const FAQS = [
  { q: "How long does it take to build my website?", a: "Your website will be live within 48 hours of you providing your content and details. We work fast so you can start getting customers sooner." },
  { q: "Do I need to provide content?", a: "We'll need basic information about your business — your services, contact details, and some photos. We can guide you through what's needed, and we also offer professional copywriting as an add-on." },
  { q: "Can I update the website myself?", a: "Yes! We'll provide you with easy-to-follow guides. Or if you'd prefer, we can manage all updates for you with our monthly support plan." },
  { q: "Is hosting included?", a: "Hosting is $99/year — one of the best value rates for Australian hosting. That covers your website, SSL, backups, and support. Domain registration (.com.au) is just $10/year." },
  { q: "What platform do you build on?", a: "We build on the best platform for your needs — WordPress, Shopify, or custom solutions. We'll recommend the right one based on your requirements." },
  { q: "Do you offer payment plans?", a: "Yes! We offer flexible payment options. Contact us to discuss what works for you." },
];

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    axios.get(`${API}/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, []);

  return (
    <main className="pt-16" data-testid="packages-page">
      <SEOHead
        title="Website Packages & Pricing"
        description="Affordable website packages for Australian businesses. Starter from $499, Business from $899, eCommerce from $1,499. All include mobile design, SEO setup, SSL, and Google Analytics. No hidden fees."
        path="/packages"
        schema={[...productSchema(packages), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Packages", path: "/packages" }])]}
      />
      <Breadcrumbs items={[{ name: "Packages & Pricing", path: "/packages" }]} />
      {/* Hero */}
      <section className="bg-[#FFD600] border-b-4 border-black section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest mb-4">Transparent Pricing</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            One Price.<br />No Surprises.
          </h1>
          <p className="text-xl font-semibold max-w-xl mx-auto">
            Everything you need to get online, grow your business, and stop paying thousands for a website.
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="section-padding" data-testid="packages-section">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`border-2 border-black rounded-2xl flex flex-col ${pkg.is_popular ? "bg-[#FFD600]" : "bg-white"}`}
                style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}
                data-testid={`package-${pkg.name?.toLowerCase()}`}
              >
                <div className={`p-8 border-b-2 border-black ${pkg.is_popular ? "bg-black text-white rounded-t-xl" : ""}`}>
                  {pkg.is_popular && (
                    <span className="bg-[#FFD600] text-black text-xs font-black px-3 py-1 rounded-full mb-3 inline-block uppercase tracking-wider">
                      Most Popular — Best Value
                    </span>
                  )}
                  {pkg.original_price && pkg.original_price > pkg.price && (
                    <span className="bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full mb-3 inline-block uppercase tracking-wider ml-2">
                      {Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% OFF
                    </span>
                  )}
                  <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{pkg.name}</h2>
                  <p className="text-sm opacity-80 mb-6">{pkg.tagline}</p>
                  <div className="flex items-end gap-2">
                    {pkg.original_price && pkg.original_price > pkg.price && (
                      <span className="text-2xl font-bold line-through opacity-50 mb-2">${pkg.original_price}</span>
                    )}
                    <span className="text-6xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>${pkg.price}</span>
                    <div className="mb-2">
                      <p className="text-sm font-bold">{pkg.period}</p>
                      <p className="text-xs opacity-70">No hidden fees</p>
                    </div>
                  </div>
                </div>
                <div className="p-8 flex-1">
                  <ul className="space-y-3 mb-8">
                    {(pkg.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <div className={`w-5 h-5 rounded-full border-2 border-black flex items-center justify-center shrink-0 mt-0.5 ${pkg.is_popular ? "bg-black" : "bg-[#FFD600]"}`}>
                          <Check size={10} className={pkg.is_popular ? "text-[#FFD600]" : "text-black"} strokeWidth={3} />
                        </div>
                        <span className="font-medium">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-8 pt-0">
                  <Link
                    to="/contact"
                    className={`block text-center font-black text-lg py-4 rounded-full border-2 border-black transition-all hover:-translate-y-1 ${
                      pkg.is_popular ? "bg-[#FFD600] text-black" : "bg-black text-white"
                    }`}
                    style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,0.3)" }}
                    data-testid={`package-get-started-${pkg.name?.toLowerCase()}`}
                  >
                    Get Started — ${pkg.price}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="addons-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-3">Power Ups</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              Optional Add-Ons
            </h2>
            <p className="text-gray-600 mt-4 text-lg">Supercharge your website with these extras</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ADDONS.map((addon, i) => (
              <div key={i} className="bg-white border-2 border-black rounded-2xl p-6 flex items-start gap-4 hover:-translate-y-1 transition-transform" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`addon-${i}`}>
                <div className="w-10 h-10 bg-[#FFD600] border-2 border-black rounded-xl flex items-center justify-center shrink-0">
                  <Plus size={18} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <h3 className="font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{addon.name}</h3>
                    <span className="text-sm font-black bg-[#FFD600] border border-black px-2 py-0.5 rounded-full">{addon.price}</span>
                  </div>
                  <p className="text-sm text-gray-600">{addon.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding" data-testid="faq-section">
        <div className="container-main max-w-3xl">
          <div className="text-center mb-14">
            <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-3">Got Questions?</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              We've Got Answers
            </h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="border-2 border-black rounded-2xl overflow-hidden"
                style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left p-6 font-bold flex items-center justify-between hover:bg-[#FFD600] transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span>{faq.q}</span>
                  <div className={`w-6 h-6 border-2 border-black rounded-full flex items-center justify-center shrink-0 ml-4 transition-transform ${openFaq === i ? "rotate-45 bg-black text-white" : "bg-[#FFD600]"}`}>
                    <Plus size={12} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 pt-2 text-gray-700 border-t-2 border-black bg-[#F4F4F5]">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-lg font-semibold mb-4">Still have questions? Let's chat!</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="faq-cta">
                Get in Touch <ArrowRight size={16} />
              </Link>
              <Link to="/faq" className="bg-white border-2 border-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                View All FAQs <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="section-padding bg-white border-t-2 border-black">
        <div className="container-main">
          <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Explore More</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { title: "Our Portfolio", desc: "See real websites we've built for Australian businesses.", link: "/portfolio" },
              { title: "How It Works", desc: "Our simple 4-step process from enquiry to launch.", link: "/how-it-works" },
              { title: "Get a Free Quote", desc: "Tell us about your project and get a custom quote.", link: "/quote" },
              { title: "About Us", desc: "Meet the Melbourne team behind No Fuss Websites.", link: "/about" },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="border-2 border-black rounded-xl p-5 hover:-translate-y-1 transition-all bg-white" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                <h3 className="font-black mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
